import React, { useState } from "react";
import { QuoteBreakdownItem, Quote, Installment } from "@/modules/service_request_manage/types/Quote";

const formatCurrency = (amount: number) =>
  amount.toLocaleString("en-IN", { style: "currency", currency: "INR" });

/* ================== COMPONENTS ================== */

const Breakdown: React.FC<{ items: QuoteBreakdownItem[] }> = ({ items }) => (
  <div className="space-y-1">
    {items.map((item, idx) => (
      <div key={idx} className="flex justify-between">
        <span>{item.label}</span>
        <span>{formatCurrency(item.amount)}</span>
      </div>
    ))}
  </div>
);

const Total: React.FC<{ amount: number }> = ({ amount }) => (
  <div className="flex justify-between border-t pt-2">
    <span>Total</span>
    <span>{formatCurrency(amount)}</span>
  </div>
);

const PaidDetails: React.FC<{ paid: number; refunded: number }> = ({ paid, refunded }) => (
  <div className="pt-2 space-y-1">
    {paid > 0 && (
      <div className="flex justify-between">
        <span>Paid</span>
        <span>{formatCurrency(paid)}</span>
      </div>
    )}
    {refunded > 0 && (
      <div className="flex justify-between text-red-600">
        <span>Refunded</span>
        <span>-{formatCurrency(refunded)}</span>
      </div>
    )}
  </div>
);

const Payable: React.FC<{ label?: string; amount: number }> = ({ label, amount }) =>
  amount > 0 ? (
    <div className="flex justify-between border-t pt-2">
      <span>{label ? `Payable (${label})` : "Payable"}</span>
      <span>{formatCurrency(amount)}</span>
    </div>
  ) : null;

const InstallmentBlock: React.FC<{ inst: Installment }> = ({ inst }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded mb-3">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left px-3 py-2 bg-gray-100 font-semibold flex justify-between"
      >
        <span>{inst.label}</span>
        <span>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="p-3 bg-white">
          <div className="flex justify-between border-t pt-2">
            <span>Total</span>
            <span>{formatCurrency(inst.amount)}</span>
          </div>
          <div className="flex justify-between bg-orange-200 border-t pt-2">
            <span>Payable ({inst.label})</span>
            <span>{formatCurrency(inst.amount)}</span>
          </div>
          <div className="text-xs text-gray-600 mt-1">
            {/* Due Date: {new Date(inst.due_date).toLocaleDateString("en-IN")} */}
          </div>
          <div className="text-xs text-gray-600">Status: {inst.status}</div>
        </div>
      )}
    </div>
  );
};

const InstallmentProgress: React.FC<{ completed: number; total: number }> = ({ completed, total }) =>
  total > 0 ? (
    <div className="px-4 py-2 text-sm text-gray-600">
      {completed}/{total} installments completed
    </div>
  ) : null;

/* ================== MAIN ================== */

interface QuoteSummaryProps {
  quote: Quote;
  showPayments?: boolean;
  showCard?: boolean;
}

const QuoteSummary: React.FC<QuoteSummaryProps> = ({ quote, showPayments = true, showCard = true }) => {
  if (!quote) return null;

  const breakdown = quote.breakdown ?? [];
  const total = quote.total ?? 0;

  const ps = quote.payment_summary;
  const payment = quote.payment;
  const mode = payment?.mode;

  const installments = quote.installments ?? [];
  const completedInstallments = installments.filter(
    (inst) => inst.status === "paid" || inst.status === "processing"
  ).length;

  const content = (
    <div className="text-sm space-y-4">
      <Breakdown items={breakdown} />
      <Total amount={total} />
      {showPayments && <PaidDetails paid={ps.total_paid} refunded={ps.total_refunded} />}
      {mode === "installment" && quote.active_inst_id ? (
        (() => {
          const current = installments.find((i) => i.id === quote.active_inst_id);
          if (!current || current.status === "paid" || current.status === "processing") return null;
          return <Payable label={current.label} amount={current.amount ?? 0} />;
        })()
      ) : (
        <Payable amount={payment?.payable ?? 0} />
      )}
      {mode === "installment" && (
        <div className="mt-4">
          <div className="font-semibold mb-2">Installments</div>
          {installments.map((inst) => (
            <InstallmentBlock key={inst.id} inst={inst} />
          ))}
        </div>
      )}
      {mode === "installment" && (
        <InstallmentProgress completed={completedInstallments} total={installments.length} />
      )}
    </div>
  );

  if (!showCard) return <div>{content}</div>;
  
  return (
    <div className="border rounded bg-gray-50 mb-4">
      <div className="px-4 py-2 bg-gray-200 font-semibold">Quote Summary</div>
      <div className="p-4">{content}</div>
    </div>
  );
};

export default QuoteSummary;